import {
  IconButton,
  Chip,
  Box,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useState, memo, useCallback, useMemo } from 'react';
import type { Band } from '../../models/types';
import { useBandStore } from '../../stores/bandStore';
import BandCustomizationModal from './BandCustomizationModal';

interface BandTileProps {
  band: Band;
}

const BandTile = ({ band }: BandTileProps) => {
  const [customizationModalOpen, setCustomizationModalOpen] = useState(false);

  // Optimized selectors - only re-render when THIS band's state changes
  const bandId = `${band.collection}|${band.source}`;

  const selected = useBandStore(
    useCallback((state) => state.selectedBands.has(bandId), [bandId])
  );

  const featured = useBandStore(
    useCallback((state) => state.featuredBands.has(bandId), [bandId])
  );

  const customized = useBandStore(
    useCallback(
      (state) =>
        state.selectedBandYears.has(band.bandName) ||
        state.selectedBandMonthYears.has(band.bandName),
      [band.bandName]
    )
  );

  const toggleBandSelection = useBandStore((state) => state.toggleBandSelection);
  const toggleBandFeatured = useBandStore((state) => state.toggleBandFeatured);

  // Flutter-style colors
  const backgroundColor = useMemo(() => {
    if (!selected) return 'transparent';
    // Use Flutter's blue palette for Live Shuffle, amber for Great 78
    return band.source === 'ls' ? 'rgba(66, 165, 245, 0.12)' : 'rgba(255, 183, 77, 0.12)';
  }, [selected, band.source]);

  const borderColor = useMemo(() => {
    if (!selected) return 'divider';
    return band.source === 'ls' ? 'primary.main' : 'warning.main';
  }, [selected, band.source]);

  const textColor = useMemo(() => {
    if (!selected) return 'text.primary';
    return band.source === 'ls' ? 'primary.main' : 'warning.main';
  }, [selected, band.source]);

  const icon = useMemo(() => {
    if (featured) {
      return selected ? 
        <StarIcon sx={{ color: 'warning.main' }} /> : 
        <StarBorderIcon sx={{ color: 'text.secondary' }} />;
    }
    return selected ? 
      <CheckCircleIcon sx={{ color: band.source === 'ls' ? 'primary.main' : 'warning.main' }} /> : 
      <CheckCircleOutlineIcon sx={{ color: 'text.secondary' }} />;
  }, [featured, selected, band.source]);

  const bandName = useMemo(() => {
    let name = band.bandName;
    if (customized) {
      name += ' (Customized)';
    }
    return name;
  }, [band.bandName, customized]);

  const tooltipText = useMemo(() => {
    if (selected) {
      if (customized) {
        return `Remove customized ${band.bandName} from shuffle list\nClick options for customization`;
      }
      return `Remove ${band.bandName} from shuffle list\nClick options for customization`;
    }
    return `Add ${band.bandName} to shuffle list\nClick options for customization`;
  }, [selected, customized, band.bandName]);

  const handleToggleSelection = useCallback(() => {
    toggleBandSelection(band);
  }, [toggleBandSelection, band]);

  const handleToggleFeatured = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    toggleBandFeatured(band);
  }, [toggleBandFeatured, band]);

  const handleOpenCustomization = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCustomizationModalOpen(true);
  }, []);

  return (
    <>
      <Tooltip title={tooltipText}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 1,
            backgroundColor,
            border: '1px solid',
            borderColor,
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: selected ? backgroundColor : 'action.hover',
              borderColor: selected ? borderColor : 'primary.main',
            },
          }}
          onClick={handleToggleSelection}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
              {icon}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ 
                  color: textColor, 
                  fontWeight: selected ? 500 : 400,
                  mb: 0.25,
                }}
                noWrap
              >
                {bandName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {band.numShows} shows • {band.numSongs} songs
                </Typography>
                {band.source === 'great78' && (
                  <Chip 
                    label="Great 78" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.65rem', 
                      height: 20,
                      '& .MuiChip-label': {
                        px: 0.75,
                      }
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
              <IconButton
                size="small"
                onClick={handleToggleFeatured}
                sx={{ 
                  color: featured ? 'warning.main' : 'action.active',
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 183, 77, 0.12)',
                  }
                }}
              >
                {featured ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleOpenCustomization}
                sx={{ 
                  color: 'action.active',
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <MoreHorizIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Tooltip>

      <BandCustomizationModal
        open={customizationModalOpen}
        onClose={() => setCustomizationModalOpen(false)}
        band={band}
      />
    </>
  );
};

// Custom comparison to prevent unnecessary re-renders
const areEqual = (prevProps: BandTileProps, nextProps: BandTileProps) => {
  return (
    prevProps.band.bandIdx === nextProps.band.bandIdx &&
    prevProps.band.collection === nextProps.band.collection &&
    prevProps.band.source === nextProps.band.source
  );
};

export default memo(BandTile, areEqual);
